import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      header: {
        title: 'BORN IN YU',
        established: 'EST. 1943',
        cart: 'Permit List',
        mobileInfo: 'DOCUMENT ARCHIVE / CLASSIFICATION: PUBLIC',
      },
      hero: {
        reference: 'Document Reference: YU-291143-A',
        country: 'Country',
        dissolved: 'Dissolved',
        spirit: 'Spirit',
        eternal: 'Eternal',
        subheading: 'Official State Archive of Merchandise & Apparel',
        description:
          'Official archival garment project. Preserving the memory of the concrete dreams.',
        classification: 'Classification: UNCLASSIFIED',
        status: 'Status: ACTIVE',
        authenticatedOn: 'Authenticated On',
        authenticatedDate: 'NOV 29 1943',
        sceneLabel: 'Scene: Archive Entry #882',
        dossierActive: 'Dossier: Active',
        statusVerified: 'Status: Verified',
        section02: 'Section 02: Resource Allocation',
        archiveAccess: 'Archive Access: Restricted',
      },
      products: {
        title: 'Catalog of Available Documents',
        subtitle: 'Heritage Textile Archive — Series 1943',
        button: 'Acquire Permit',
        viewDetails: 'View Details',
        visitStore: 'Visit our store',
        serialPrefix: 'Serial Ref:',
        documentId: 'Document ID:',
        docNo: 'DOC NO.',
        classification: 'CLASSIFICATION',
        issuanceCost: 'ISSUANCE COST from',
        items: {
          tshirt: 'T-Shirt',
          hoodie: 'Hoodie',
          totebag: 'Tote Bag',
          poster: 'Poster',
          mug: 'Ceramic Mug',
        },
      },
      about: {
        classification: 'Document Classification',
        paragraph1:
          'BORN IN YU is an archival project dedicated to preserving the cultural memory of Yugoslavia through material artifacts and textile documentation.',
        paragraph2:
          'Founded on the principles of brutalist aesthetics and socialist modernism, we celebrate the concrete dreams and unrepeatable spirit of a dissolved nation that lives on in the hearts of its people.',
        paragraph3:
          'Our "Bureaucratic Chic" aesthetic emerged from the unique visual language of Yugoslav institutions—the heavy typewriter fonts on official documents, the bold geometric stamps, the utilitarian beauty of government forms. This wasn\'t just paperwork; it was a distinct design philosophy that shaped an era.',
        paragraph4:
          'From the monumental Spomeniks scattered across the Balkans to the Yugo car that became a symbol of everyday life, Yugoslav design represented a fusion of socialist ideals and modernist innovation. We preserve this heritage through apparel and artifacts that honor the aesthetic of a nation that valued solidarity, brutalist architecture, and functional beauty.',
        quote: 'A country can disappear from the map, but it cannot disappear from memory.',
        paragraph5:
          'Each product in our collection represents an official permit to remember, authenticate, and celebrate the legacy of Yugoslav culture, architecture, and identity. Whether you lived it, your parents told stories about it, or you\'re discovering this unique aesthetic for the first time—Born in YU connects you to a powerful cultural moment.',
        stat1Label: 'Year of Foundation',
        stat2Label: 'Republics United',
        stat3Label: 'Spirit: Eternal',
      },
      contact: {
        classification: 'Communication Protocol',
        officialRegistry: 'Official Registry',
        archiveLocation: 'Archive Location',
        subjectClassification: 'Subject Classification',
        subjectPlaceholder: 'ENTER SUBJECT',
        emailAddress: 'Email Address',
        emailPlaceholder: 'YOUR@EMAIL.COM',
        messageContent: 'Message Content',
        messagePlaceholder: 'ENTER YOUR MESSAGE...',
        submitRequest: 'Submit Request',
      },
      shipping: {
        classification: 'Distribution Protocol',
        processingTime: 'Processing Time',
        processingText:
          'All orders are processed within 3-5 business days. Orders placed on weekends or holidays will be processed the following business day.',
        shippingMethods: 'Shipping Methods',
        standardShipping: 'Standard Shipping: 7-14 business days',
        expressShipping: 'Express Shipping: 3-5 business days',
        internationalShipping: 'International Shipping: 14-21 business days',
        shippingCosts: 'Shipping Costs',
        shippingCostsText:
          'Shipping costs are calculated at checkout based on destination and selected shipping method.',
        trackingInfo: 'Tracking Information',
        trackingInfoText:
          'Once your order has shipped, you will receive a confirmation email with tracking information. Please allow 24 hours for tracking to activate.',
        internationalCustoms: 'International Customs',
        internationalCustomsText:
          'International orders may be subject to import duties and taxes. The recipient is responsible for any customs fees or import taxes imposed by their country.',
      },
      returns: {
        classification: 'Return Documentation',
        returnWindow: 'Return Window',
        returnWindowText:
          'Items may be returned within 30 days of delivery for a full refund. Items must be unworn, unwashed, and in original condition with all tags attached.',
        returnProcess: 'Return Process',
        returnStep1: 'Contact our support team at returns@borninyu.com',
        returnStep2: 'Receive return authorization and shipping label',
        returnStep3: 'Package item securely with original packaging',
        returnStep4: 'Ship within 7 days of receiving authorization',
        returnStep5: 'Refund processed within 5-7 business days of receipt',
        nonReturnable: 'Non-Returnable Items',
        nonReturnableItem1: 'Custom or personalized items',
        nonReturnableItem2: 'Sale or clearance items',
        nonReturnableItem3: 'Items damaged by customer',
        exchanges: 'Exchanges',
        exchangesText:
          'We currently do not offer direct exchanges. Please return the item for a refund and place a new order for the desired item.',
        damagedItems: 'Damaged or Defective Items',
        damagedItemsText:
          'If you receive a damaged or defective item, please contact us immediately at support@borninyu.com with photos of the item and packaging. We will arrange a replacement or full refund at no cost to you.',
      },
      privacy: {
        classification: 'Confidential Document',
        lastUpdated: 'Last Updated: November 2024',
        infoWeCollect: 'Information We Collect',
        infoWeCollectText:
          'We collect information you provide directly, such as name, email, shipping address, and payment information when you make a purchase or create an account.',
        howWeUse: 'How We Use Your Information',
        useReason1: 'Process and fulfill your orders',
        useReason2: 'Send order confirmations and shipping updates',
        useReason3: 'Respond to your inquiries and support requests',
        useReason4: 'Send promotional emails (with your consent)',
        useReason5: 'Improve our website and customer experience',
        infoSharing: 'Information Sharing',
        infoSharingText:
          'We do not sell or rent your personal information to third parties. We may share your information with service providers who assist in operating our website, processing payments, and fulfilling orders.',
        dataSecurity: 'Data Security',
        dataSecurityText:
          'We implement appropriate security measures to protect your personal information from unauthorized access, alteration, or disclosure.',
        yourRights: 'Your Rights',
        right1: 'Access the personal data we hold about you',
        right2: 'Request correction of inaccurate data',
        right3: 'Request deletion of your data',
        right4: 'Opt-out of marketing communications',
        contactUs: 'Contact Us',
        contactUsText: 'For privacy-related inquiries, contact us at privacy@borninyu.com',
      },
      terms: {
        classification: 'Legal Documentation',
        lastUpdated: 'Last Updated: November 2024',
        acceptance: 'Acceptance of Terms',
        acceptanceText:
          'By accessing and using this website, you accept and agree to be bound by the terms and provisions of this agreement.',
        useLicense: 'Use License',
        useLicenseText:
          "Permission is granted to temporarily download one copy of materials on BORN IN YU's website for personal, non-commercial transitory viewing only.",
        prohibited: 'Prohibited Uses',
        prohibitedItem1: 'Violating any applicable laws or regulations',
        prohibitedItem2: 'Impersonating another person or entity',
        prohibitedItem3: 'Interfering with website security features',
        prohibitedItem4: 'Using automated systems to access the site',
        productInfo: 'Product Information',
        productInfoText:
          "We strive to display product colors and images as accurately as possible. However, we cannot guarantee that your device's display will accurately reflect the actual product color.",
        pricing: 'Pricing',
        pricingText:
          'All prices are subject to change without notice. We reserve the right to modify or discontinue products without notice.',
        limitation: 'Limitation of Liability',
        limitationText:
          'BORN IN YU shall not be liable for any indirect, incidental, special, or consequential damages arising out of your use of the website or products.',
        governingLaw: 'Governing Law',
        governingLawText:
          'These terms shall be governed by and construed in accordance with applicable international commerce laws.',
      },
      virtualSpomenik: {
        classification: 'Virtual Monument Program',
        heroTitle: 'Commission Your Virtual Spomenik',
        heroSubtitle: 'Erect an eternal monument in someone\'s name. Receive an official certificate with Tito\'s signature, bureaucratic stamps, and a unique registry number.',
        officialProgram: 'New Official Program',
        beginProcess: 'Begin Dedication Process',
        step1Title: 'Select Your Monument Style',
        step1Subtitle: 'Choose from 8 officially approved designs',
        step2Title: 'Dedication Details',
        step2Subtitle: 'Complete the official dedication form',
        step3Title: 'Select Your Package',
        step3Subtitle: 'All packages include official government approval',
        monumentStyles: {
          pillar: 'The Abstract Pillar',
          pillarDesc: 'Brutalist concrete column reaching skyward',
          flame: 'Eternal Flame',
          flameDesc: 'Modernist torch of eternal remembrance',
          rings: 'Unity Rings',
          ringsDesc: 'Interlocking circles of brotherhood and unity',
          fist: 'The Fist',
          fistDesc: 'Socialist realist worker\'s strength',
          wings: 'Memorial Wings',
          wingsDesc: 'Abstract bird symbolizing victory and freedom',
          spiral: 'The Spiral',
          spiralDesc: 'Brutalist spiral ascending to eternity',
          star: 'Star Monument',
          starDesc: 'Five-pointed star of Yugoslav unity',
          obelisk: 'The Obelisk',
          obeliskDesc: 'Classic memorial pillar standing eternal',
        },
        selectMonument: 'Select This Monument',
        formLabels: {
          monumentName: 'Monument Name',
          monumentNamePlaceholder: 'Enter monument name',
          dedicateeName: 'Dedicated To',
          dedicateeNamePlaceholder: 'Enter dedicatee name',
          dedicationMessage: 'Dedication Message',
          dedicationMessagePlaceholder: 'Enter your dedication message (max 50 words)',
          location: 'Location',
          locationPlaceholder: 'Enter location or leave blank for auto-assigned coordinates',
          purchaserName: 'Your Name',
          purchaserNamePlaceholder: 'Enter your full name',
          purchaserEmail: 'Email Address',
          required: '*',
        },
        packages: {
          basic: 'Basic Package',
          basicSubtitle: 'The People\'s Certificate',
          basicPrice: '$19.99',
          basicFeature1: 'Digital certificate (PDF)',
          basicFeature2: 'Unique registry number',
          basicFeature3: 'Listed on website registry',
          basicFeature4: 'Social media graphic',
          premium: 'Premium Package',
          premiumSubtitle: 'The Party Member\'s Edition',
          premiumPrice: '$39.99',
          premiumFeature1: 'Everything in Basic',
          premiumFeature2: 'Printed certificate (8.5x11")',
          premiumFeature3: '3D monument render',
          premiumFeature4: 'Dedication plaque design',
          premiumFeature5: 'Official envelope',
          deluxe: 'Deluxe Package',
          deluxeSubtitle: 'The Supreme Soviet\'s Choice',
          deluxePrice: '$89.99',
          deluxeFeature1: 'Everything in Premium',
          deluxeFeature2: 'Framed certificate (11x14")',
          deluxeFeature3: 'Metal pin badge',
          deluxeFeature4: 'Custom monument design',
          deluxeFeature5: 'Numbered limited edition',
          mostPopular: 'Most Popular',
        },
        buttons: {
          back: '← Back',
          continue: 'Continue →',
          proceedToPayment: 'Proceed to Payment →',
        },
        certificatePreview: {
          title: 'What You\'ll Receive',
          subtitle: 'An official-looking Yugoslav government certificate complete with Tito\'s signature, bureaucratic stamps, and unique registry number',
          certificatePreview: 'Certificate Preview',
          includedFeatures: 'Included Features',
          feature1: 'Authentic Yugoslav aesthetic design',
          feature2: 'Unique registry number (YU-SPO-2025-####)',
          feature3: 'Reproduction of Tito\'s signature',
          feature4: 'Multiple official stamps and seals',
          feature5: 'Personalized dedication message',
          perfectGiftFor: 'Perfect Gift For',
          gift1: 'Yugoslav history enthusiasts',
          gift2: 'Balkan nostalgia collectors',
          gift3: 'Brutalist architecture fans',
          gift4: 'Parents, grandparents who remember Yugoslavia',
          gift5: 'Unique wedding/engagement gifts',
        },
        disclaimer: {
          title: 'DISCLAIMER',
          text1: 'This is a novelty item for entertainment purposes only. No actual monument will be erected. The certificate is a commemorative keepsake and holds no legal authority. The Socialist Federal Republic of Yugoslavia no longer exists. Josip Broz Tito is deceased and did not sign this document (obviously). This is satire and homage to Yugoslav memorial culture.',
          text2: 'By purchasing, you acknowledge this is a virtual/symbolic dedication, similar to "name a star" services. All proceeds support the preservation of actual Yugoslav monuments through our heritage programs.',
        },
      },
      footer: {
        tagline: 'Official State Archive of Merchandise & Apparel',
        quickLinks: 'Quick Links',
        legal: 'Legal',
        connect: 'Connect',
        newsletter: 'Newsletter Subscription',
        newsletterText: 'Subscribe to receive official documents and archive updates.',
        emailPlaceholder: 'Enter official email address',
        subscribe: 'Submit Application',
        links: {
          about: 'About Archive',
          products: 'Product Catalog',
          contact: 'Contact Bureau',
          archive: 'The Archive',
          manifesto: 'Manifesto 1943',
          dossier: 'Modernist Dossier',
          spomenikMaps: 'Spomenik Maps',
          shipping: 'Shipping Protocol',
          returns: 'Return Policy',
          privacy: 'Privacy Charter',
          terms: 'Terms of Service',
        },
        copyright: '© 1943-2024 BORN IN YU. Eternal Archive Rights.',
      },
      backToTop: 'Back to Top',
      // Manifesto Page
      manifesto: {
        docId: 'DOC ID',
        classified: 'DECLASSIFIED',
        title: 'MANIFESTO 1943',
        subtitle: 'A Declaration of Eternal Memory',
        issued: 'ISSUED',
        approved: 'APPROVED',
        verified: 'VERIFIED',
        article1: {
          title: 'Our Mission',
          content:
            'We preserve the memory of a nation that transcended borders and time. Through authentic merchandise and archival documentation, we keep alive the spirit of Yugoslav unity, modernism, and cultural achievement. This is not nostalgia—this is documentation.',
        },
        article2: {
          title: 'What We Preserve',
          content:
            'Brutalist monuments that touch the sky. Socialist modernist design that defined an era. Industrial achievements that built a nation. Cultural artifacts that united six republics. The concrete dreams that shaped generations. All authenticated, all documented, all eternal.',
        },
        article3: {
          title: 'Why We Remember',
          content:
            'A country can dissolve, but its spirit cannot. The Yugoslav experience was unique—a federation built on brotherhood and unity, expressed through radical architecture and progressive design. We document this heritage before it fades from memory, ensuring future generations understand what once was.',
        },
        article4: {
          title: 'The Concrete Dreams',
          content:
            'From Genex Tower to Sutjeska Memorial, from Pionir Hall to Avala Tower—these structures were more than buildings. They were statements of ambition, unity, and modernist vision. Each monument tells a story of a nation that dared to dream in concrete and steel.',
        },
        article5: {
          title: 'For Future Generations',
          content:
            'We are not historians. We are archivists of memory. Every product we create carries the weight of documentation. Every design references authentic sources. We ensure that those born after 1991 can touch, wear, and experience the material culture of a dissolved nation. This archive is eternal.',
        },
        footer: {
          authority: 'FEDERAL HERITAGE PRESERVATION AUTHORITY',
          official: 'OFFICIAL',
          declassified: 'DECLASSIFIED',
          forPosterity: 'FOR POSTERITY',
        },
      },
      // Archive Page
      archive: {
        securityClearance: 'SECURITY CLEARANCE',
        totalArtifacts: 'TOTAL ARTIFACTS',
        title: 'THE ARCHIVE',
        subtitle:
          'A chronological repository of Yugoslav cultural artifacts, monuments, and design heritage. All items authenticated and documented for preservation.',
        docId: 'DOC ID',
        year: 'YEAR',
        noResults: 'NO ARTIFACTS FOUND',
        noResultsDescription:
          'No artifacts match the selected category. Please try a different filter.',
        categories: {
          all: 'ALL ARCHIVES',
          architecture: 'ARCHITECTURE',
          design: 'DESIGN',
          industry: 'INDUSTRY',
          culture: 'CULTURE',
          sports: 'SPORTS',
        },
        status: {
          declassified: 'DECLASSIFIED',
          classified: 'CLASSIFIED',
          restricted: 'RESTRICTED',
        },
        artifacts: {
          '1': {
            title: 'Avala Tower Communications Monument',
            description:
              'Iconic telecommunications tower built in 1965, destroyed in 1999, reconstructed in 2010. Symbol of Yugoslav technological achievement and modernist design.',
          },
          '2': {
            title: 'Yugo 45 Automobile Blueprint',
            description:
              'Technical documentation for the iconic Yugo 45, produced from 1980-2008. Represents Yugoslav automotive industry and mass production capability.',
          },
          '3': {
            title: 'Genex Tower Architectural Plans',
            description:
              "Original blueprints for the Western City Gate (Genex Tower), designed by Mihajlo Mitrović. Completed in 1980, stands as Belgrade's brutalist landmark.",
          },
          '4': {
            title: 'Sarajevo Winter Olympics Emblem',
            description:
              'Official emblem and design materials from the 1984 Winter Olympics held in Sarajevo. Celebrated Yugoslav organizational excellence and winter sports heritage.',
          },
          '5': {
            title: 'Sutjeska Memorial Complex',
            description:
              'Photographic documentation of the Battle of Sutjeska memorial by sculptor Miodrag Živković. Completed in 1971, commemorates partisan victory.',
          },
          '6': {
            title: 'Red Star Belgrade 1991 European Cup',
            description:
              "Archival materials documenting Red Star Belgrade's European Cup victory in 1991, the last major Yugoslav sporting achievement before dissolution.",
          },
        },
      },
      // Modernist Dossier Page
      dossier: {
        classification: 'DESIGN ARCHIVE - CLASSIFIED',
        totalEntries: 'TOTAL ENTRIES',
        title: 'MODERNIST DOSSIER',
        subtitle:
          'A curated collection of Yugoslav modernist design. Product design, graphic design, industrial achievements, and furniture that defined an era.',
        fileNo: 'FILE NO',
        designer: 'DESIGNER',
        year: 'YEAR',
        location: 'LOCATION',
        confidential: 'CONFIDENTIAL',
        noResults: 'NO ENTRIES FOUND',
        noResultsDescription: 'No design entries match the selected category.',
        historicalDocuments: 'HISTORICAL DOCUMENTS',
        articlesLabel: 'ARTICLES',
        categories: {
          all: 'ALL DESIGNS',
          product: 'PRODUCT DESIGN',
          graphic: 'GRAPHIC DESIGN',
          industrial: 'INDUSTRIAL DESIGN',
          furniture: 'FURNITURE',
        },
        status: {
          preserved: 'PRESERVED',
          deteriorating: 'DETERIORATING',
          destroyed: 'DESTROYED',
        },
        entries: {
          '1': {
            title: 'Rade Končar Electric Locomotive',
            description:
              'Series 441/461 electric locomotive designed for Yugoslav Railways. Revolutionary design combining functionality with modernist aesthetics, produced from 1955 to 1990.',
            location: 'Zagreb, Croatia',
          },
          '2': {
            title: 'Yugo-nostalgia Poster Series',
            description:
              'Iconic graphic design campaign celebrating Yugoslav cultural identity. Bold typography and socialist symbolism merged with modernist principles.',
            location: 'Belgrade, Serbia',
          },
          '3': {
            title: 'Zastava 750 "Fića" Design',
            description:
              "The people's car of Yugoslavia. Compact, efficient, and designed for mass production. Over 923,000 units produced, becoming a symbol of Yugoslav mobility.",
            location: 'Kragujevac, Serbia',
          },
          '4': {
            title: 'Rex Chair by Niko Kralj',
            description:
              'Stackable plywood chair designed in 1956. Exported globally and still in production today. Represents peak of Yugoslav furniture design.',
            location: 'Ljubljana, Slovenia',
          },
          '5': {
            title: "Sarajevo '84 Olympic Identity",
            description:
              'Complete visual identity system for 1984 Winter Olympics. Mascot Vučko became iconic. Modern, playful design recognized worldwide.',
            location: 'Sarajevo, Bosnia',
          },
          '6': {
            title: 'Gorenje Retro Refrigerator',
            description:
              'Household appliance combining curved forms with modern technology. Color options and chrome details defined Yugoslav kitchen aesthetics.',
            location: 'Velenje, Slovenia',
          },
        },
        footer: {
          note: 'All designs authenticated through Federal Design Registry / Preservation status verified 2024',
        },
        modal: {
          confidential: 'CONFIDENTIAL',
          fileNumber: 'FILE NO',
          designer: 'DESIGNER',
          year: 'YEAR',
          location: 'LOCATION',
          description: 'DESCRIPTION',
          technicalSpecs: 'TECHNICAL SPECIFICATIONS',
          historicalContext: 'HISTORICAL CONTEXT',
          historicalContextDescription:
            "This design represents Yugoslav self-management socialism's emphasis on industrial modernization and cultural identity. These objects embodied aspirations of progress while maintaining connection to regional traditions.",
        },
      },
      // Blog Article Page
      blog: {
        backToDossier: 'Back to Dossier',
        relatedProducts: 'Shop Related Products',
        readTime: 'min read',
        publishedOn: 'Published on',
        author: 'Author',
        category: 'Category',
        tags: 'Tags',
        imagePlaceholder: 'Image Coming Soon',
        navigation: {
          home: 'Home',
          dossier: 'Dossier',
          article: 'Article',
        },
      },
      // Spomenik Maps Page
      spomenik: {
        totalMonuments: 'TOTAL MONUMENTS',
        title: 'SPOMENIK MAPS',
        subtitle:
          'Interactive documentation of Yugoslav partisan memorials and brutalist monuments. Architectural landmarks of remembrance across former Yugoslavia.',
        infoBox:
          'Spomeniks (monuments) were built throughout Yugoslavia to commemorate sites of WWII battles and partisan victories. Designed by renowned architects, these brutalist structures combine abstract forms with historical memory.',
        architect: 'ARCHITECT',
        year: 'BUILT',
        city: 'CITY',
        coordinates: 'GPS',
        noResults: 'NO MONUMENTS FOUND',
        noResultsDescription: 'No monuments found for the selected country.',
        countries: {
          all: 'ALL COUNTRIES',
          serbia: 'SERBIA',
          croatia: 'CROATIA',
          bosnia: 'BOSNIA & HERZEGOVINA',
          slovenia: 'SLOVENIA',
          macedonia: 'NORTH MACEDONIA',
          montenegro: 'MONTENEGRO',
        },
        condition: {
          excellent: 'EXCELLENT',
          good: 'GOOD',
          deteriorating: 'DETERIORATING',
          destroyed: 'DESTROYED',
        },
        monuments: {
          '1': {
            name: 'Jasenovac Flower Monument',
            description:
              'Giant concrete flower commemorating victims of Jasenovac concentration camp. Six petals represent different ethnicities unified in suffering. UNESCO protected site.',
          },
          '2': {
            name: 'Podgarić Monument',
            description:
              'Abstract wings rising from the earth, honoring partisan soldiers. Stainless steel structure reflecting sky and landscape. Iconic brutalist design.',
          },
          '3': {
            name: 'Sutjeska Battle Monument',
            description:
              'Two massive concrete wings representing Battle of Sutjeska (1943). Sculptural forms integrated with natural mountain landscape. National monument.',
          },
          '4': {
            name: 'Makedonium - Ilinden Memorial',
            description:
              'Unique circular structure with stained glass windows. Commemorates Ilinden Uprising of 1903. Combines Byzantine and modernist architecture.',
          },
          '5': {
            name: 'Stone Flower Kamenska',
            description:
              'Lotus-shaped memorial designed by Vojin Bakić. Honors children killed in WWII. Stark white concrete contrasts with green landscape.',
          },
          '6': {
            name: 'Partisan Memorial Cemetery Kolašin',
            description:
              'Modernist cemetery complex with geometric memorial structures. Honors fallen partisans. Mountainous setting adds dramatic atmosphere.',
          },
        },
        legend: {
          title: 'CONDITION LEGEND',
        },
        modal: {
          viewOnMaps: 'VIEW ON GOOGLE MAPS',
          description: 'DESCRIPTION',
          visitingInfo: 'VISITING INFORMATION',
          visitingDescription:
            'Most spomeniks are located in public parks or natural settings and are accessible year-round. Please treat these monuments with respect as they are important historical and cultural heritage sites.',
        },
      },
    },
  },
  sr: {
    translation: {
      header: {
        title: 'BORN IN YU',
        established: 'OSN. 1943',
        cart: 'Lista Dozvola',
        mobileInfo: 'ARHIV DOKUMENATA / KLASIFIKACIJA: JAVNO',
      },
      hero: {
        reference: 'Referenca Dokumenta: YU-291143-A',
        country: 'Država',
        dissolved: 'Raspuštena',
        spirit: 'Duh',
        eternal: 'Večan',
        subheading: 'Službena Državna Arhiva Robe i Odeće',
        description: 'Zvanični arhivski projekat odeće. Čuvanje sećanja na betonske snove.',
        classification: 'Klasifikacija: NEKLASIFIKOVANO',
        status: 'Status: AKTIVAN',
        authenticatedOn: 'Overeno Dana',
        authenticatedDate: '29 NOV 1943',
        sceneLabel: 'Scena: Arhivski Unos #882',
        dossierActive: 'Dosije: Aktivan',
        statusVerified: 'Status: Verifikovan',
        section02: 'Sekcija 02: Alokacija Resursa',
        archiveAccess: 'Pristup Arhivi: Ograničen',
      },
      products: {
        title: 'Katalog Dostupnih Dokumenata',
        subtitle: 'Arhiva Naslednog Tekstila — Serija 1943',
        button: 'Pribavi Dozvolu',
        viewDetails: 'Pogledaj Detalje',
        visitStore: 'Posjetite našu trgovinu',
        serialPrefix: 'Serijska Ref:',
        documentId: 'ID Dokumenta:',
        docNo: 'BR. DOK.',
        classification: 'KLASIFIKACIJA',
        issuanceCost: 'CENA IZDAVANJA od',
        items: {
          tshirt: 'Majica',
          hoodie: 'Duks',
          totebag: 'Torba',
          poster: 'Poster',
          mug: 'Keramička Šolja',
        },
      },
      about: {
        classification: 'Klasifikacija Dokumenta',
        paragraph1:
          'BORN IN YU je arhivski projekat posvećen očuvanju kulturne memorije Jugoslavije kroz materijalne artefakte i tekstilnu dokumentaciju.',
        paragraph2:
          'Osnovan na principima brutalističke estetike i socijalističkog modernizma, slavimo betonske snove i neponovljivi duh raspuštene nacije koja živi u srcima njenih ljudi.',
        paragraph3:
          'Naša "Birokratska Šik" estetika proizašla je iz jedinstvenog vizuelnog jezika jugoslovenskih institucija—teških fontova pisaćih mašina na zvaničnim dokumentima, smelih geometrijskih pečata, utilitarne lepote državnih formulara. Ovo nije bila samo papirologij a; bila je to jedinstvena filozofija dizajna koja je oblikovala čitavu eru.',
        paragraph4:
          'Od monumentalnih Spomenika rasutih širom Balkana do automobila Yugo koji je postao simbol svakodnevnog života, jugoslovenski dizajn predstavljao je fuziju socijalističkih ideala i modernističkih inovacija. Čuvamo ovo nasleđe kroz odeću i artefakte koji poštuju estetiku nacije koja je cenila solidarnost, brutalističku arhitekturu i funkcionalnu lepotu.',
        quote: 'Država može nestati sa mape, ali ne može nestati iz sećanja.',
        paragraph5:
          'Svaki proizvod u našoj kolekciji predstavlja službenu dozvolu da se sećamo, autentifikujemo i slavimo nasleđe jugoslovenske kulture, arhitekture i identiteta. Bilo da ste to živeli, da su vam roditelji pričali priče o tome, ili otkrivate ovu jedinstvenu estetiku po prvi put—Born in YU vas povezuje sa moćnim kulturnim trenutkom.',
        stat1Label: 'Godina Osnivanja',
        stat2Label: 'Ujedinjenih Republika',
        stat3Label: 'Duh: Večan',
      },
      contact: {
        classification: 'Protokol Komunikacije',
        officialRegistry: 'Službeni Registar',
        archiveLocation: 'Lokacija Arhive',
        subjectClassification: 'Klasifikacija Predmeta',
        subjectPlaceholder: 'UNESITE PREDMET',
        emailAddress: 'Email Adresa',
        emailPlaceholder: 'VAŠA@EMAIL.COM',
        messageContent: 'Sadržaj Poruke',
        messagePlaceholder: 'UNESITE VAŠU PORUKU...',
        submitRequest: 'Pošalji Zahtev',
      },
      shipping: {
        classification: 'Protokol Distribucije',
        processingTime: 'Vreme Obrade',
        processingText:
          'Sve porudžbine se obrađuju u roku od 3-5 radnih dana. Porudžbine napravljene vikendom ili praznicima će biti obrađene sledećeg radnog dana.',
        shippingMethods: 'Metode Isporuke',
        standardShipping: 'Standardna Isporuka: 7-14 radnih dana',
        expressShipping: 'Ekspresna Isporuka: 3-5 radnih dana',
        internationalShipping: 'Međunarodna Isporuka: 14-21 radni dan',
        shippingCosts: 'Troškovi Isporuke',
        shippingCostsText:
          'Troškovi isporuke se izračunavaju pri plaćanju na osnovu destinacije i izabrane metode isporuke.',
        trackingInfo: 'Informacije o Praćenju',
        trackingInfoText:
          'Kada vaša porudžbina bude poslata, dobićete email za potvrdu sa informacijama o praćenju. Molimo dozvolite 24 sata za aktivaciju praćenja.',
        internationalCustoms: 'Međunarodna Carina',
        internationalCustomsText:
          'Međunarodne porudžbine mogu biti podložne uvoznim carinama i taksama. Primalac je odgovoran za sve carinske troškove ili uvozne takse nametnute od strane njihove države.',
      },
      returns: {
        classification: 'Dokumentacija Povraćaja',
        returnWindow: 'Period za Povraćaj',
        returnWindowText:
          'Artikli se mogu vratiti u roku od 30 dana od dostave za pun povraćaj novca. Artikli moraju biti nenošeni, neопрани i u originalnom stanju sa svim etiketama.',
        returnProcess: 'Proces Povraćaja',
        returnStep1: 'Kontaktirajte naš tim za podršku na returns@borninyu.com',
        returnStep2: 'Primite ovlašćenje za povraćaj i etiketu za slanje',
        returnStep3: 'Sigurno upakujte artikal sa originalnim pakovanjem',
        returnStep4: 'Pošaljite u roku od 7 dana od prijema ovlašćenja',
        returnStep5: 'Povraćaj novca obrađen u roku od 5-7 radnih dana od prijema',
        nonReturnable: 'Artikli Koji se Ne Mogu Vratiti',
        nonReturnableItem1: 'Prilagođeni ili personalizovani artikli',
        nonReturnableItem2: 'Artikli na rasprodaji ili sniženju',
        nonReturnableItem3: 'Artikli oštećeni od strane kupca',
        exchanges: 'Zamene',
        exchangesText:
          'Trenutno ne nudimo direktne zamene. Molimo vratite artikal za povraćaj novca i napravite novu porudžbinu za željeni artikal.',
        damagedItems: 'Oštećeni ili Neispravni Artikli',
        damagedItemsText:
          'Ako dobijete oštećen ili neispravan artikal, molimo kontaktirajte nas odmah na support@borninyu.com sa fotografijama artikla i pakovanja. Organizovaćemo zamenu ili pun povraćaj novca bez troškova za vas.',
      },
      privacy: {
        classification: 'Poverljiv Dokument',
        lastUpdated: 'Poslednje Ažurirano: Novembar 2024',
        infoWeCollect: 'Informacije Koje Prikupljamo',
        infoWeCollectText:
          'Prikupljamo informacije koje direktno pružate, kao što su ime, email, adresa za isporuku i informacije o plaćanju kada obavljate kupovinu ili kreirate nalog.',
        howWeUse: 'Kako Koristimo Vaše Informacije',
        useReason1: 'Obrađujemo i ispunjavamo vaše porudžbine',
        useReason2: 'Šaljemo potvrde porudžbina i ažuriranja o isporuci',
        useReason3: 'Odgovaramo na vaše upite i zahteve za podršku',
        useReason4: 'Šaljemo promotivne emailove (uz vašu saglasnost)',
        useReason5: 'Poboljšavamo našu veb stranicu i korisničko iskustvo',
        infoSharing: 'Deljenje Informacija',
        infoSharingText:
          'Ne prodajemo niti iznajmljujemo vaše lične informacije trećim stranama. Možemo deliti vaše informacije sa pružaocima usluga koji pomažu u radu naše veb stranice, obradi plaćanja i ispunjenju porudžbina.',
        dataSecurity: 'Bezbednost Podataka',
        dataSecurityText:
          'Primenjujemo odgovarajuće bezbednosne mere da zaštitimo vaše lične informacije od neovlašćenog pristupa, izmene ili otkrivanja.',
        yourRights: 'Vaša Prava',
        right1: 'Pristup ličnim podacima koje držimo o vama',
        right2: 'Zahtev za ispravku netačnih podataka',
        right3: 'Zahtev za brisanje vaših podataka',
        right4: 'Odjava sa marketinških komunikacija',
        contactUs: 'Kontaktirajte Nas',
        contactUsText: 'Za upite vezane za privatnost, kontaktirajte nas na privacy@borninyu.com',
      },
      terms: {
        classification: 'Pravna Dokumentacija',
        lastUpdated: 'Poslednje Ažurirano: Novembar 2024',
        acceptance: 'Prihvatanje Uslova',
        acceptanceText:
          'Pristupanjem i korišćenjem ove veb stranice, prihvatate i slažete se da budete vezani uslovima i odredbama ovog sporazuma.',
        useLicense: 'Licenca za Upotrebu',
        useLicenseText:
          'Dozvola je data za privremeno preuzimanje jedne kopije materijala sa veb stranice BORN IN YU samo za lični, nekomercijalni privremeni pregled.',
        prohibited: 'Zabranjena Upotreba',
        prohibitedItem1: 'Kršenje bilo kojih važećih zakona ili propisa',
        prohibitedItem2: 'Lažno predstavljanje kao druga osoba ili entitet',
        prohibitedItem3: 'Ometanje bezbednosnih funkcija veb stranice',
        prohibitedItem4: 'Korišćenje automatizovanih sistema za pristup sajtu',
        productInfo: 'Informacije o Proizvodu',
        productInfoText:
          'Trudimo se da prikažemo boje i slike proizvoda što je tačnije moguće. Međutim, ne možemo garantovati da će ekran vašeg uređaja tačno odražavati stvarnu boju proizvoda.',
        pricing: 'Cene',
        pricingText:
          'Sve cene podležu promenama bez najave. Zadržavamo pravo da modifikujemo ili ukinemo proizvode bez najave.',
        limitation: 'Ograničenje Odgovornosti',
        limitationText:
          'BORN IN YU neće biti odgovoran za bilo kakvu indirektnu, slučajnu, specijalnu ili posledičnu štetu koja proizilazi iz vaše upotrebe veb stranice ili proizvoda.',
        governingLaw: 'Merodavno Pravo',
        governingLawText:
          'Ovi uslovi će biti regulisani i tumačeni u skladu sa primenljivim zakonima međunarodne trgovine.',
      },
      footer: {
        tagline: 'Službena Državna Arhiva Robe i Odeće',
        quickLinks: 'Brze Veze',
        legal: 'Pravno',
        connect: 'Povežite Se',
        newsletter: 'Pretplata na Bilten',
        newsletterText: 'Pretplatite se da primite službene dokumente i ažuriranja arhive.',
        emailPlaceholder: 'Unesite službenu email adresu',
        subscribe: 'Pošalji Prijavu',
        links: {
          about: 'O Arhivi',
          products: 'Katalog Proizvoda',
          contact: 'Kontakt Biro',
          archive: 'Arhiva',
          manifesto: 'Manifest 1943',
          dossier: 'Modernistički Dosije',
          spomenikMaps: 'Mapa Spomenika',
          shipping: 'Protokol Isporuke',
          returns: 'Politika Povraćaja',
          privacy: 'Povelja o Privatnosti',
          terms: 'Uslovi Korišćenja',
        },
        copyright: '© 1943-2024 BORN IN YU. Večna Prava Arhive.',
      },
      backToTop: 'Nazad na Vrh',
      dossier: {
        classification: 'ARHIVA DIZAJNA - KLASIFIKOVANO',
        totalEntries: 'UKUPNO UNOSA',
        title: 'MODERNISTIČKI DOSIJE',
        subtitle: 'Kolekcija jugoslovenskog modernističkog dizajna. Dizajn proizvoda, grafički dizajn, industrijska dostignuća i nameštaj koji su definisali jednu eru.',
        fileNo: 'BROJ DOSIJEA',
        designer: 'DIZAJNER',
        year: 'GODINA',
        location: 'LOKACIJA',
        confidential: 'POVERLJIVO',
        noResults: 'NEMA UNOSA',
        historicalDocuments: 'ISTORIJSKI DOKUMENTI',
        articlesLabel: 'ČLANCI',
        noResultsDescription: 'Nema dizajnerskih unosa za izabranu kategoriju.',
        categories: {
          all: 'SVI DIZAJNI',
          product: 'PROIZVOD',
          graphic: 'GRAFIKA',
          industrial: 'INDUSTRIJA',
          furniture: 'NAMEŠTAJ',
        },
        status: { preserved: 'OČUVANO', deteriorating: 'PROPADA', destroyed: 'UNIŠTENO' },
        entries: {
          '1': {
            title: 'Rade Končar Električna Lokomotiva',
            description:
              'Serija 441/461 električna lokomotiva dizajnirana za Jugoslovenske Železnice.',
            location: 'Zagreb, Hrvatska',
          },
          '2': {
            title: 'Yugo-nostalgija Poster Serija',
            description: 'Ikonična grafička kampanja slavlja jugoslovenskog identiteta.',
            location: 'Beograd, Srbija',
          },
          '3': {
            title: 'Zastava 750 "Fića" Dizajn',
            description: 'Narodni auto Jugoslavije.',
            location: 'Kragujevac, Srbija',
          },
          '4': {
            title: 'Rex Stolica Niko Kralj',
            description: 'Slagajuća šper-ploča stolica dizajnirana 1956.',
            location: 'Ljubljana, Slovenija',
          },
          '5': {
            title: "Sarajevo '84 Olimpijski Identitet",
            description: 'Kompletan vizuelni identitet za ZOI 1984.',
            location: 'Sarajevo, Bosna',
          },
          '6': {
            title: 'Gorenje Retro Frižider',
            description: 'Kućni aparat kombinujući zakrivljene forme.',
            location: 'Velenje, Slovenija',
          },
        },
        footer: { note: 'Svi dizajni autentifikovani kroz Savezni Registar Dizajna' },
        modal: {
          confidential: 'POVERLJIVO',
          fileNumber: 'BROJ DOSIJEA',
          designer: 'DIZAJNER',
          year: 'GODINA',
          location: 'LOKACIJA',
          description: 'OPIS',
          technicalSpecs: 'TEHNIČKE SPECIFIKACIJE',
          historicalContext: 'ISTORIJSKI KONTEKST',
          historicalContextDescription:
            'Ovaj dizajn predstavlja jugoslovenski samoupravni socijalizam sa naglaskom na industrijsku modernizaciju i kulturni identitet.',
        },
      },
      blog: {
        backToDossier: 'Nazad na Dosije',
        relatedProducts: 'Pregledaj Povezane Proizvode',
        readTime: 'min čitanja',
        publishedOn: 'Objavljeno',
        author: 'Autor',
        category: 'Kategorija',
        tags: 'Oznake',
        imagePlaceholder: 'Slika Uskoro',
        navigation: {
          home: 'Početna',
          dossier: 'Dosije',
          article: 'Članak',
        },
      },
      spomenik: {
        totalMonuments: 'UKUPNO SPOMENIKA',
        title: 'MAPA SPOMENIKA',
        subtitle: 'Interaktivna dokumentacija jugoslovenskih partizanskih spomen-obeležja.',
        infoBox:
          'Spomenici su građeni širom Jugoslavije da obeleže mesta bitaka iz Drugog svetskog rata.',
        architect: 'ARHITEKTA',
        year: 'IZGRAĐENO',
        city: 'GRAD',
        coordinates: 'GPS',
        noResults: 'NEMA SPOMENIKA',
        noResultsDescription: 'Nema spomenika za izabranu državu.',
        countries: {
          all: 'SVE DRŽAVE',
          serbia: 'SRBIJA',
          croatia: 'HRVATSKA',
          bosnia: 'BOSNA I HERCEGOVINA',
          slovenia: 'SLOVENIJA',
          macedonia: 'SEVERNA MAKEDONIJA',
          montenegro: 'CRNA GORA',
        },
        condition: {
          excellent: 'ODLIČNO',
          good: 'DOBRO',
          deteriorating: 'PROPADA',
          destroyed: 'UNIŠTENO',
        },
        monuments: {
          '1': {
            name: 'Jasenovac Kameni Cvet',
            description: 'Gigantski betonski cvet u sećanje na žrtve logora Jasenovac.',
          },
          '2': {
            name: 'Podgarić Spomenik',
            description: 'Apstraktna krila koja se uzdižu iz zemlje.',
          },
          '3': {
            name: 'Sutjeska Spomenik Bitke',
            description: 'Dva masivna betonska krila predstavljaju Bitku na Sutjesci.',
          },
          '4': {
            name: 'Makedonium - Ilinden Memorijal',
            description: 'Jedinstvena kružna struktura sa vitražima.',
          },
          '5': {
            name: 'Kameni Cvet Kamenska',
            description: 'Memorijal u obliku lotosa dizajnirao Vojin Bakić.',
          },
          '6': {
            name: 'Partizansko Spomen Groblje Kolašin',
            description: 'Modernistički kompleks groblja.',
          },
        },
        legend: { title: 'LEGENDA STANJA' },
        modal: {
          viewOnMaps: 'PRIKAŽI NA GOOGLE MAPAMA',
          description: 'OPIS',
          visitingInfo: 'INFORMACIJE ZA POSETU',
          visitingDescription:
            'Većina spomenika se nalazi u javnim parkovima ili prirodnim okruženjima i dostupni su tokom cele godine. Molimo tretirajte ove spomenike sa poštovanjem.',
        },
      },
      // Manifesto Page
      manifesto: {
        docId: 'ID DOKUMENTA',
        classified: 'DEKLASIFIKOVANO',
        title: 'MANIFEST 1943',
        subtitle: 'Deklaracija Večne Memorije',
        issued: 'IZDATO',
        approved: 'ODOBRENO',
        verified: 'VERIFIKOVANO',
        article1: {
          title: 'Naša Misija',
          content:
            'Čuvamo sećanje na naciju koja je prevazišla granice i vreme. Kroz autentičnu robu i arhivsku dokumentaciju, održavamo živim duh jugoslovenskog jedinstva, modernizma i kulturnih dostignuća. Ovo nije nostalgija—ovo je dokumentacija.',
        },
        article2: {
          title: 'Šta Čuvamo',
          content:
            'Brutalističke spomenike koji dodiruju nebo. Socijalistički modernistički dizajn koji je definisao eru. Industrijska dostignuća koja su izgradila naciju. Kulturne artefakte koji su ujedinili šest republika. Betonske snove koji su oblikovali generacije. Sve autentifikovano, sve dokumentovano, sve večno.',
        },
        article3: {
          title: 'Zašto Se Sećamo',
          content:
            'Država može da se raspadne, ali njen duh ne može. Jugoslovensko iskustvo je bilo jedinstveno—federacija izgrađena na bratstvu i jedinstvu, izražena kroz radikalnu arhitekturu i progresivni dizajn. Dokumentujemo ovo nasleđe pre nego što nestane iz sećanja, osiguravajući da buduće generacije razumeju šta je nekada bilo.',
        },
        article4: {
          title: 'Betonski Snovi',
          content:
            'Od Geneks kule do Memorijala Sutjeska, od Pionir hale do Avalskog tornja—ove strukture su bile više od zgrada. Bile su izjave ambicije, jedinstva i modernističke vizije. Svaki spomenik priča priču o naciji koja se usudila da sanja u betonu i čeliku.',
        },
        article5: {
          title: 'Za Buduće Generacije',
          content:
            'Mi nismo istoričari. Mi smo arhivisti sećanja. Svaki proizvod koji kreiramo nosi težinu dokumentacije. Svaki dizajn referencira autentične izvore. Osiguravamo da oni rođeni posle 1991. mogu da dodirnu, nose i iskuse materijalnu kulturu raspuštene nacije. Ova arhiva je večna.',
        },
        footer: {
          authority: 'SAVEZNI ORGAN ZA OČUVANJE NASLEĐA',
          official: 'ZVANIČNO',
          declassified: 'DEKLASIFIKOVANO',
          forPosterity: 'ZA POTOMSTVO',
        },
      },
      // Archive Page
      archive: {
        securityClearance: 'BEZBEDNOSNA DOZVOLA',
        totalArtifacts: 'UKUPNO ARTEFAKATA',
        title: 'ARHIVA',
        subtitle:
          'Hronološki repozitorijum jugoslovenskih kulturnih artefakata, spomenika i dizajnerskog nasleđa. Svi predmeti autentifikovani i dokumentovani za očuvanje.',
        docId: 'ID DOK',
        year: 'GODINA',
        noResults: 'NEMA ARTEFAKATA',
        noResultsDescription:
          'Nema artefakata koji odgovaraju izabranoj kategoriji. Molimo pokušajte drugi filter.',
        categories: {
          all: 'SVE ARHIVE',
          architecture: 'ARHITEKTURA',
          design: 'DIZAJN',
          industry: 'INDUSTRIJA',
          culture: 'KULTURA',
          sports: 'SPORT',
        },
        status: {
          declassified: 'DEKLASIFIKOVANO',
          classified: 'KLASIFIKOVANO',
          restricted: 'OGRANIČENO',
        },
        artifacts: {
          '1': {
            title: 'Avala Toranj Komunikacioni Spomenik',
            description:
              'Kultni telekomunikacioni toranj izgrađen 1965, uništen 1999, rekonstruisan 2010. Simbol jugoslovenskog tehnološkog dostignuća i modernističkog dizajna.',
          },
          '2': {
            title: 'Yugo 45 Automobilski Plan',
            description:
              'Tehnička dokumentacija za kultni Yugo 45, proizveden od 1980-2008. Predstavlja jugoslovensku automobilsku industriju i masovnu proizvodnju.',
          },
          '3': {
            title: 'Geneks Kula Arhitektonski Planovi',
            description:
              'Originalni planovi za Zapadna Kapija Beograda (Geneks Kula), dizajnirao Mihajlo Mitrović. Završena 1980, stoji kao brutalistički orijentir Beograda.',
          },
          '4': {
            title: 'Sarajevo Zimske Olimpijade Amblem',
            description:
              'Zvanični amblem i dizajnerski materijali sa Zimskih Olimpijskih igara 1984 održanih u Sarajevu. Slavi jugoslovensku organizacionu izvrsnost i nasleđe zimskih sportova.',
          },
          '5': {
            title: 'Sutjeska Memorijalni Kompleks',
            description:
              'Fotografska dokumentacija memorijalnog kompleksa Bitke na Sutjesci vajara Miodraga Živkovića. Završen 1971, obeležava partizansku pobedu.',
          },
          '6': {
            title: 'Crvena Zvezda 1991 Evropski Kup',
            description:
              'Arhivski materijali koji dokumentuju pobedu Crvene zvezde na Evropskom kupu 1991, poslednje veliko jugoslovensko sportsko dostignuće pre raspada.',
          },
        },
      },
    },
  },
  hr: {
    translation: {
      header: {
        title: 'BORN IN YU',
        established: 'OSN. 1943',
        cart: 'Popis Dozvola',
        mobileInfo: 'ARHIVA DOKUMENATA / KLASIFIKACIJA: JAVNO',
      },
      hero: {
        reference: 'Referenca Dokumenta: YU-291143-A',
        country: 'Država',
        dissolved: 'Raspuštena',
        spirit: 'Duh',
        eternal: 'Vječan',
        subheading: 'Službena Državna Arhiva Robe i Odjeće',
        description: 'Službeni arhivski projekt odjeće. Čuvanje sjećanja na betonske snove.',
        classification: 'Klasifikacija: NEKLASIFICIRANO',
        status: 'Status: AKTIVAN',
        authenticatedOn: 'Ovjereno Dana',
        authenticatedDate: '29 STU 1943',
        sceneLabel: 'Scena: Arhivski Unos #882',
        dossierActive: 'Dosije: Aktivan',
        statusVerified: 'Status: Verificiran',
        section02: 'Odjeljak 02: Raspodjela Resursa',
        archiveAccess: 'Pristup Arhivi: Ograničen',
      },
      products: {
        title: 'Katalog Dostupnih Dokumenata',
        subtitle: 'Arhiva Nasljeda Tekstila — Serija 1943',
        button: 'Pribavi Dozvolu',
        viewDetails: 'Pogledaj Detalje',
        visitStore: 'Posjetite našu trgovinu',
        serialPrefix: 'Serijska Ref:',
        documentId: 'ID Dokumenta:',
        docNo: 'BR. DOK.',
        classification: 'KLASIFIKACIJA',
        issuanceCost: 'CIJENA IZDAVANJA od',
        items: {
          tshirt: 'Majica',
          hoodie: 'Duks',
          totebag: 'Torba',
          poster: 'Poster',
          mug: 'Keramička Šalica',
        },
      },
      about: {
        classification: 'Klasifikacija Dokumenta',
        paragraph1:
          'BORN IN YU je arhivski projekt posvećen očuvanju kulturne memorije Jugoslavije kroz materijalne artefakte i tekstilnu dokumentaciju.',
        paragraph2:
          'Osnovan na principima brutalističke estetike i socijalističkog modernizma, slavimo betonske snove i neponovljivi duh raspuštene nacije koja živi u srcima svojih ljudi.',
        paragraph3:
          'Naša "Birokratska Šik" estetika proizašla je iz jedinstvenog vizualnog jezika jugoslavenskih institucija—teških fontova pisaćih strojeva na službenim dokumentima, hrabrih geometrijskih pečata, utilitarne ljepote državnih obrazaca. Ovo nije bila samo papirologij a; bila je to jedinstvena filozofija dizajna koja je oblikovala čitavu eru.',
        paragraph4:
          'Od monumentalnih Spomenika raštrkanih širom Balkana do automobila Yugo koji je postao simbol svakodnevnog života, jugoslavenski dizajn predstavljao je fuziju socijalističkih ideala i modernističkih inovacija. Čuvamo ovu baštinu kroz odjeću i artefakte koji poštuju estetiku nacije koja je cijenila solidarnost, brutalističku arhitekturu i funkcionalnu ljepotu.',
        quote: 'Država može nestati s karte, ali ne može nestati iz sjećanja.',
        paragraph5:
          'Svaki proizvod u našoj kolekciji predstavlja službenu dozvolu da se sjećamo, autentificiramo i slavimo naslijeđe jugoslavenske kulture, arhitekture i identiteta. Bilo da ste to živjeli, da su vam roditelji pričali priče o tome, ili otkrivate ovu jedinstvenu estetiku po prvi put—Born in YU vas povezuje s moćnim kulturnim trenutkom.',
        stat1Label: 'Godina Osnivanja',
        stat2Label: 'Ujedinjenih Republika',
        stat3Label: 'Duh: Vječan',
      },
      contact: {
        classification: 'Protokol Komunikacije',
        officialRegistry: 'Službeni Registar',
        archiveLocation: 'Lokacija Arhive',
        subjectClassification: 'Klasifikacija Predmeta',
        subjectPlaceholder: 'UNESITE PREDMET',
        emailAddress: 'Email Adresa',
        emailPlaceholder: 'VAŠ@EMAIL.COM',
        messageContent: 'Sadržaj Poruke',
        messagePlaceholder: 'UNESITE VAŠU PORUKU...',
        submitRequest: 'Pošalji Zahtjev',
      },
      shipping: {
        classification: 'Protokol Distribucije',
        processingTime: 'Vrijeme Obrade',
        processingText:
          'Sve narudžbe se obrađuju u roku od 3-5 radnih dana. Narudžbe napravljene vikendom ili blagdanima će biti obrađene sljedećeg radnog dana.',
        shippingMethods: 'Metode Dostave',
        standardShipping: 'Standardna Dostava: 7-14 radnih dana',
        expressShipping: 'Ekspresna Dostava: 3-5 radnih dana',
        internationalShipping: 'Međunarodna Dostava: 14-21 radni dan',
        shippingCosts: 'Troškovi Dostave',
        shippingCostsText:
          'Troškovi dostave se izračunavaju pri plaćanju na osnovu odredišta i odabrane metode dostave.',
        trackingInfo: 'Informacije o Praćenju',
        trackingInfoText:
          'Kada vaša narudžba bude poslana, primit ćete email za potvrdu s informacijama o praćenju. Molimo dopustite 24 sata za aktivaciju praćenja.',
        internationalCustoms: 'Međunarodna Carina',
        internationalCustomsText:
          'Međunarodne narudžbe mogu biti podložne uvoznim carinama i porezima. Primatelj je odgovoran za sve carinske troškove ili uvozne poreze nametnute od strane njihove države.',
      },
      returns: {
        classification: 'Dokumentacija Povrata',
        returnWindow: 'Period za Povrat',
        returnWindowText:
          'Artikli se mogu vratiti u roku od 30 dana od dostave za puni povrat novca. Artikli moraju biti nenošeni, neопрани i u izvornom stanju sa svim etiketama.',
        returnProcess: 'Proces Povrata',
        returnStep1: 'Kontaktirajte naš tim za podršku na returns@borninyu.com',
        returnStep2: 'Primite ovlaštenje za povrat i naljepnicu za slanje',
        returnStep3: 'Sigurno upakujte artikal s izvornim pakovanjem',
        returnStep4: 'Pošaljite u roku od 7 dana od prijema ovlaštenja',
        returnStep5: 'Povrat novca obrađen u roku od 5-7 radnih dana od prijema',
        nonReturnable: 'Artikli Koji se Ne Mogu Vratiti',
        nonReturnableItem1: 'Prilagođeni ili personalizirani artikli',
        nonReturnableItem2: 'Artikli na rasprodaji ili sniženju',
        nonReturnableItem3: 'Artikli oštećeni od strane kupca',
        exchanges: 'Zamjene',
        exchangesText:
          'Trenutno ne nudimo izravne zamjene. Molimo vratite artikal za povrat novca i napravite novu narudžbu za željeni artikal.',
        damagedItems: 'Oštećeni ili Neispravni Artikli',
        damagedItemsText:
          'Ako dobijete oštećen ili neispravan artikal, molimo kontaktirajte nas odmah na support@borninyu.com s fotografijama artikla i pakiranja. Organizirat ćemo zamjenu ili puni povrat novca bez troškova za vas.',
      },
      privacy: {
        classification: 'Povjerljiv Dokument',
        lastUpdated: 'Zadnje Ažurirano: Studeni 2024',
        infoWeCollect: 'Informacije Koje Prikupljamo',
        infoWeCollectText:
          'Prikupljamo informacije koje izravno pružate, kao što su ime, email, adresa za dostavu i informacije o plaćanju kada obavljate kupnju ili kreirate račun.',
        howWeUse: 'Kako Koristimo Vaše Informacije',
        useReason1: 'Obrađujemo i ispunjavamo vaše narudžbe',
        useReason2: 'Šaljemo potvrde narudžbi i ažuriranja o dostavi',
        useReason3: 'Odgovaramo na vaše upite i zahtjeve za podršku',
        useReason4: 'Šaljemo promotivne emailove (uz vašu suglasnost)',
        useReason5: 'Poboljšavamo našu web stranicu i korisničko iskustvo',
        infoSharing: 'Dijeljenje Informacija',
        infoSharingText:
          'Ne prodajemo niti iznajmljujemo vaše osobne informacije trećim stranama. Možemo dijeliti vaše informacije s pružateljima usluga koji pomažu u radu naše web stranice, obradi plaćanja i ispunjenju narudžbi.',
        dataSecurity: 'Sigurnost Podataka',
        dataSecurityText:
          'Primjenjujemo odgovarajuće sigurnosne mjere da zaštitimo vaše osobne informacije od neovlaštenog pristupa, izmjene ili otkrivanja.',
        yourRights: 'Vaša Prava',
        right1: 'Pristup osobnim podacima koje držimo o vama',
        right2: 'Zahtjev za ispravak netočnih podataka',
        right3: 'Zahtjev za brisanje vaših podataka',
        right4: 'Odjava s marketinških komunikacija',
        contactUs: 'Kontaktirajte Nas',
        contactUsText: 'Za upite vezane uz privatnost, kontaktirajte nas na privacy@borninyu.com',
      },
      terms: {
        classification: 'Pravna Dokumentacija',
        lastUpdated: 'Zadnje Ažurirano: Studeni 2024',
        acceptance: 'Prihvaćanje Uvjeta',
        acceptanceText:
          'Pristupanjem i korištenjem ove web stranice, prihvaćate i slažete se da budete vezani uvjetima i odredbama ovog sporazuma.',
        useLicense: 'Licenca za Upotrebu',
        useLicenseText:
          'Dozvola je dana za privremeno preuzimanje jedne kopije materijala s web stranice BORN IN YU samo za osobni, nekomercijalni privremeni pregled.',
        prohibited: 'Zabranjena Upotreba',
        prohibitedItem1: 'Kršenje bilo kojih važećih zakona ili propisa',
        prohibitedItem2: 'Lažno predstavljanje kao druga osoba ili entitet',
        prohibitedItem3: 'Ometanje sigurnosnih funkcija web stranice',
        prohibitedItem4: 'Korištenje automatiziranih sustava za pristup stranici',
        productInfo: 'Informacije o Proizvodu',
        productInfoText:
          'Trudimo se da prikažemo boje i slike proizvoda što je točnije moguće. Međutim, ne možemo jamčiti da će zaslon vašeg uređaja točno odražavati stvarnu boju proizvoda.',
        pricing: 'Cijene',
        pricingText:
          'Sve cijene podliježu promjenama bez najave. Zadržavamo pravo da modificiramo ili ukinemo proizvode bez najave.',
        limitation: 'Ograničenje Odgovornosti',
        limitationText:
          'BORN IN YU neće biti odgovoran za bilo kakvu neizravnu, slučajnu, posebnu ili posljedičnu štetu koja proizlazi iz vaše upotrebe web stranice ili proizvoda.',
        governingLaw: 'Mjerodavno Pravo',
        governingLawText:
          'Ovi uvjeti će biti regulirani i tumačeni u skladu s primjenjivim zakonima međunarodne trgovine.',
      },
      footer: {
        tagline: 'Službena Državna Arhiva Robe i Odjeće',
        quickLinks: 'Brze Veze',
        legal: 'Pravno',
        connect: 'Povežite Se',
        newsletter: 'Pretplata na Bilten',
        newsletterText: 'Pretplatite se da primite službene dokumente i ažuriranja arhive.',
        emailPlaceholder: 'Unesite službenu email adresu',
        subscribe: 'Pošalji Prijavu',
        links: {
          about: 'O Arhivi',
          products: 'Katalog Proizvoda',
          contact: 'Kontakt Ured',
          archive: 'Arhiva',
          manifesto: 'Manifest 1943',
          shipping: 'Protokol Dostave',
          returns: 'Politika Povrata',
          privacy: 'Povelja o Privatnosti',
          terms: 'Uvjeti Korištenja',
        },
        copyright: '© 1943-2024 BORN IN YU. Vječna Prava Arhive.',
      },
      backToTop: 'Natrag na Vrh',
      dossier: {
        classification: 'ARHIVA DIZAJNA - KLASIFICIRANO',
        totalEntries: 'UKUPNO UNOSA',
        title: 'MODERNISTIČKI DOSJE',
        subtitle: 'Kolekcija jugoslovenskog modernističkog dizajna. Dizajn proizvoda, grafički dizajn, industrijska dostignuća i namještaj koji su definisali jednu eru.',
        fileNo: 'BROJ DOSIJEA',
        designer: 'DIZAJNER',
        year: 'GODINA',
        location: 'LOKACIJA',
        confidential: 'POVJERLJIVO',
        noResults: 'NEMA UNOSA',
        historicalDocuments: 'ИСТОРИЈСКИ ДОКУМЕНТИ',
        articlesLabel: 'ЧЛАНЦИ',
        noResultsDescription: 'Nema dizajnerskih unosa za odabranu kategoriju.',
        categories: {
          all: 'SVI DIZAJNI',
          product: 'PROIZVOD',
          graphic: 'GRAFIKA',
          industrial: 'INDUSTRIJA',
          furniture: 'NAMJEŠTAJ',
        },
        status: { preserved: 'OČUVANO', deteriorating: 'PROPADA', destroyed: 'UNIŠTENO' },
        entries: {
          '1': {
            title: 'Rade Končar Električna Lokomotiva',
            description:
              'Serija 441/461 električna lokomotiva dizajnirana za Jugoslavenske Željeznice.',
            location: 'Zagreb, Hrvatska',
          },
          '2': {
            title: 'Yugo-nostalgija Poster Serija',
            description: 'Ikonična grafička kampanja slavlja jugoslavenskog identiteta.',
            location: 'Beograd, Srbija',
          },
          '3': {
            title: 'Zastava 750 "Fića" Dizajn',
            description: 'Narodni auto Jugoslavije.',
            location: 'Kragujevac, Srbija',
          },
          '4': {
            title: 'Rex Stolica Niko Kralj',
            description: 'Slagajuća šper-ploča stolica dizajnirana 1956.',
            location: 'Ljubljana, Slovenija',
          },
          '5': {
            title: "Sarajevo '84 Olimpijski Identitet",
            description: 'Kompletan vizualni identitet za ZOI 1984.',
            location: 'Sarajevo, Bosna',
          },
          '6': {
            title: 'Gorenje Retro Hladnjak',
            description: 'Kućanski aparat kombinujući zakrivljene forme.',
            location: 'Velenje, Slovenija',
          },
        },
        footer: { note: 'Svi dizajni autentificirani kroz Savezni Registar Dizajna' },
        modal: {
          confidential: 'POVJERLJIVO',
          fileNumber: 'BROJ DOSIJEA',
          designer: 'DIZAJNER',
          year: 'GODINA',
          location: 'LOKACIJA',
          description: 'OPIS',
          technicalSpecs: 'TEHNIČKE SPECIFIKACIJE',
          historicalContext: 'POVIJESNI KONTEKST',
          historicalContextDescription:
            'Ovaj dizajn predstavlja jugoslavenski samoupravni socijalizam s naglaskom na industrijsku modernizaciju i kulturni identitet.',
        },
      },
      blog: {
        backToDossier: 'Natrag na Dosje',
        relatedProducts: 'Pregledaj Povezane Proizvode',
        readTime: 'min čitanja',
        publishedOn: 'Objavljeno',
        author: 'Autor',
        category: 'Kategorija',
        tags: 'Oznake',
        imagePlaceholder: 'Slika Uskoro',
        navigation: {
          home: 'Početna',
          dossier: 'Dosje',
          article: 'Članak',
        },
      },
      spomenik: {
        totalMonuments: 'UKUPNO SPOMENIKA',
        title: 'MAPA SPOMENIKA',
        subtitle: 'Interaktivna dokumentacija jugoslavenskih partizanskih spomen-obilježja.',
        infoBox:
          'Spomenici su građeni širom Jugoslavije da obilježe mjesta bitaka iz Drugog svjetskog rata.',
        architect: 'ARHITEKT',
        year: 'IZGRAĐENO',
        city: 'GRAD',
        coordinates: 'GPS',
        noResults: 'NEMA SPOMENIKA',
        noResultsDescription: 'Nema spomenika za odabranu državu.',
        countries: {
          all: 'SVE DRŽAVE',
          serbia: 'SRBIJA',
          croatia: 'HRVATSKA',
          bosnia: 'BOSNA I HERCEGOVINA',
          slovenia: 'SLOVENIJA',
          macedonia: 'SJEVERNA MAKEDONIJA',
          montenegro: 'CRNA GORA',
        },
        condition: {
          excellent: 'ODLIČNO',
          good: 'DOBRO',
          deteriorating: 'PROPADA',
          destroyed: 'UNIŠTENO',
        },
        monuments: {
          '1': {
            name: 'Jasenovac Kameni Cvijet',
            description: 'Gigantski betonski cvijet u sjećanje na žrtve logora Jasenovac.',
          },
          '2': {
            name: 'Podgarić Spomenik',
            description: 'Apstraktna krila koja se uzdižu iz zemlje.',
          },
          '3': {
            name: 'Sutjeska Spomenik Bitke',
            description: 'Dva masivna betonska krila predstavljaju Bitku na Sutjesci.',
          },
          '4': {
            name: 'Makedonium - Ilinden Memorijal',
            description: 'Jedinstvena kružna struktura s vitražima.',
          },
          '5': {
            name: 'Kameni Cvijet Kamenska',
            description: 'Memorijal u obliku lotosa dizajnirao Vojin Bakić.',
          },
          '6': {
            name: 'Partizansko Spomen Groblje Kolašin',
            description: 'Modernistički kompleks groblja.',
          },
        },
        legend: { title: 'LEGENDA STANJA' },
        modal: {
          viewOnMaps: 'PRIKAŽI NA GOOGLE KARTAMA',
          description: 'OPIS',
          visitingInfo: 'INFORMACIJE ZA POSJET',
          visitingDescription:
            'Većina spomenika se nalazi u javnim parkovima ili prirodnim okruženjima i dostupni su tijekom cijele godine. Molimo tretirajte ove spomenike s poštovanjem.',
        },
      },
      // Manifesto Page
      manifesto: {
        docId: 'ID DOKUMENTA',
        classified: 'DEKLASIFICIRANO',
        title: 'MANIFEST 1943',
        subtitle: 'Deklaracija Vječne Memorije',
        issued: 'IZDANO',
        approved: 'ODOBRENO',
        verified: 'VERIFICIRANO',
        article1: {
          title: 'Naša Misija',
          content:
            'Čuvamo sjećanje na naciju koja je prevazišla granice i vrijeme. Kroz autentičnu robu i arhivsku dokumentaciju, održavamo živim duh jugoslavenskog jedinstva, modernizma i kulturnih dostignuća. Ovo nije nostalgija—ovo je dokumentacija.',
        },
        article2: {
          title: 'Što Čuvamo',
          content:
            'Brutalističke spomenike koji diraju nebo. Socijalistički modernistički dizajn koji je definirao eru. Industrijska dostignuća koja su izgradila naciju. Kulturne artefakte koji su ujedinili šest republika. Betonske snove koji su oblikovali generacije. Sve autentificirano, sve dokumentirano, sve vječno.',
        },
        article3: {
          title: 'Zašto Se Sjećamo',
          content:
            'Država može nestati, ali njen duh ne može. Jugoslavensko iskustvo bilo je jedinstveno—federacija izgrađena na bratstvu i jedinstvu, izražena kroz radikalnu arhitekturu i progresivni dizajn. Dokumentiramo ovo naslijeđe prije nego što nestane iz sjećanja, osiguravajući da buduće generacije razumiju što je nekada bilo.',
        },
        article4: {
          title: 'Betonski Snovi',
          content:
            'Od Genex kule do Memorijala Sutjeska, od Pionir dvorane do Avalskog tornja—ove strukture bile su više od zgrada. Bile su izjave ambicije, jedinstva i modernističke vizije. Svaki spomenik priča priču o naciji koja se usudila sanjati u betonu i čeliku.',
        },
        article5: {
          title: 'Za Buduće Generacije',
          content:
            'Mi nismo povjesničari. Mi smo arhivisti sjećanja. Svaki proizvod koji kreiramo nosi težinu dokumentacije. Svaki dizajn referencira autentične izvore. Osiguravamo da oni rođeni nakon 1991. mogu dodirnuti, nositi i iskusiti materijalnu kulturu raspuštene nacije. Ova arhiva je vječna.',
        },
        footer: {
          authority: 'SAVEZNI ORGAN ZA OČUVANJE NASLIJEĐA',
          official: 'SLUŽBENO',
          declassified: 'DEKLASIFICIRANO',
          forPosterity: 'ZA POTOMSTVO',
        },
      },
      // Archive Page
      archive: {
        securityClearance: 'SIGURNOSNA DOZVOLA',
        totalArtifacts: 'UKUPNO ARTEFAKATA',
        title: 'ARHIVA',
        subtitle:
          'Kronološki repozitorij jugoslavenskih kulturnih artefakata, spomenika i dizajnerskog naslijeđa. Svi predmeti autentificirani i dokumentirani za očuvanje.',
        docId: 'ID DOK',
        year: 'GODINA',
        noResults: 'NEMA ARTEFAKATA',
        noResultsDescription:
          'Nema artefakata koji odgovaraju odabranoj kategoriji. Molimo pokušajte drugi filter.',
        categories: {
          all: 'SVE ARHIVE',
          architecture: 'ARHITEKTURA',
          design: 'DIZAJN',
          industry: 'INDUSTRIJA',
          culture: 'KULTURA',
          sports: 'SPORT',
        },
        status: {
          declassified: 'DEKLASIFICIRANO',
          classified: 'KLASIFICIRANO',
          restricted: 'OGRANIČENO',
        },
        artifacts: {
          '1': {
            title: 'Avala Toranj Komunikacijski Spomenik',
            description:
              'Kultni telekomunikacijski toranj izgrađen 1965, uništen 1999, rekonstruiran 2010. Simbol jugoslavenskog tehnološkog dostignuća i modernističkog dizajna.',
          },
          '2': {
            title: 'Yugo 45 Automobilski Plan',
            description:
              'Tehnička dokumentacija za kultni Yugo 45, proizveden od 1980-2008. Predstavlja jugoslavensku automobilsku industriju i masovnu proizvodnju.',
          },
          '3': {
            title: 'Genex Kula Arhitektonski Planovi',
            description:
              'Originalni planovi za Zapadna Vrata Beograda (Genex Kula), dizajnirao Mihajlo Mitrović. Završena 1980, stoji kao brutalistički orijentir Beograda.',
          },
          '4': {
            title: 'Sarajevo Zimske Olimpijade Amblem',
            description:
              'Službeni amblem i dizajnerski materijali sa Zimskih Olimpijskih igara 1984 održanih u Sarajevu. Slavi jugoslavensku organizacijsku izvrsnost i naslijeđe zimskih sportova.',
          },
          '5': {
            title: 'Sutjeska Memorijalni Kompleks',
            description:
              'Fotografska dokumentacija memorijalnog kompleksa Bitke na Sutjesci kipara Miodraga Živkovića. Završen 1971, obilježava partizansku pobjedu.',
          },
          '6': {
            title: 'Crvena Zvezda 1991 Europski Kup',
            description:
              'Arhivski materijali koji dokumentiraju pobjedu Crvene zvezde na Europskom kupu 1991, posljednje veliko jugoslavensko sportsko dostignuće prije raspada.',
          },
        },
      },
    },
  },
  bs: {
    translation: {
      header: {
        title: 'BORN IN YU',
        established: 'OSN. 1943',
        cart: 'Lista Dozvola',
        mobileInfo: 'ARHIV DOKUMENATA / KLASIFIKACIJA: JAVNO',
      },
      hero: {
        reference: 'Referenca Dokumenta: YU-291143-A',
        country: 'Država',
        dissolved: 'Raspuštena',
        spirit: 'Duh',
        eternal: 'Vječan',
        subheading: 'Službena Državna Arhiva Robe i Odjeće',
        description: 'Službeni arhivski projekt odjeće. Čuvanje sjećanja na betonske snove.',
        classification: 'Klasifikacija: NEKLASIFICIRANO',
        status: 'Status: AKTIVAN',
        authenticatedOn: 'Ovjereno Dana',
        authenticatedDate: '29 NOV 1943',
        sceneLabel: 'Scena: Arhivski Unos #882',
        dossierActive: 'Dosije: Aktivan',
        statusVerified: 'Status: Verificiran',
        section02: 'Sekcija 02: Raspodjela Resursa',
        archiveAccess: 'Pristup Arhivi: Ograničen',
      },
      products: {
        title: 'Katalog Dostupnih Dokumenata',
        subtitle: 'Arhiva Nasljednog Tekstila — Serija 1943',
        button: 'Pribavi Dozvolu',
        viewDetails: 'Pogledaj Detalje',
        visitStore: 'Posetite našu prodavnicu',
        serialPrefix: 'Serijska Ref:',
        documentId: 'ID Dokumenta:',
        docNo: 'BR. DOK.',
        classification: 'KLASIFIKACIJA',
        issuanceCost: 'CIJENA IZDAVANJA od',
        items: {
          tshirt: 'Majica',
          hoodie: 'Duks',
          totebag: 'Torba',
          poster: 'Poster',
          mug: 'Keramička Šolja',
        },
      },
      about: {
        classification: 'Klasifikacija Dokumenta',
        paragraph1:
          'BORN IN YU je arhivski projekt posvećen očuvanju kulturne memorije Jugoslavije kroz materijalne artefakte i tekstilnu dokumentaciju.',
        paragraph2:
          'Osnovan na principima brutalističke estetike i socijalističkog modernizma, slavimo betonske snove i neponovljivi duh raspuštene nacije koja živi u srcima svojih ljudi.',
        paragraph3:
          'Naša "Birokratska Šik" estetika proizašla je iz jedinstvenog vizualnog jezika jugoslavenskih institucija—teških fontova pisaćih strojeva na službenim dokumentima, hrabrih geometrijskih pečata, utilitarne ljepote državnih obrazaca. Ovo nije bila samo papirologij a; bila je to jedinstvena filozofija dizajna koja je oblikovala čitavu eru.',
        paragraph4:
          'Od monumentalnih Spomenika raštrkanih širom Balkana do automobila Yugo koji je postao simbol svakodnevnog života, jugoslavenski dizajn predstavljao je fuziju socijalističkih ideala i modernističkih inovacija. Čuvamo ovu baštinu kroz odjeću i artefakte koji poštuju estetiku nacije koja je cijenila solidarnost, brutalističku arhitekturu i funkcionalnu ljepotu.',
        quote: 'Država može nestati s karte, ali ne može nestati iz sjećanja.',
        paragraph5:
          'Svaki proizvod u našoj kolekciji predstavlja službenu dozvolu da se sjećamo, autentificiramo i slavimo naslijeđe jugoslavenske kulture, arhitekture i identiteta. Bilo da ste to živjeli, da su vam roditelji pričali priče o tome, ili otkrivate ovu jedinstvenu estetiku po prvi put—Born in YU vas povezuje s moćnim kulturnim trenutkom.',
        stat1Label: 'Godina Osnivanja',
        stat2Label: 'Ujedinjenih Republika',
        stat3Label: 'Duh: Vječan',
      },
      contact: {
        classification: 'Protokol Komunikacije',
        officialRegistry: 'Službeni Registar',
        archiveLocation: 'Lokacija Arhive',
        subjectClassification: 'Klasifikacija Predmeta',
        subjectPlaceholder: 'UNESITE PREDMET',
        emailAddress: 'Email Adresa',
        emailPlaceholder: 'VAŠ@EMAIL.COM',
        messageContent: 'Sadržaj Poruke',
        messagePlaceholder: 'UNESITE VAŠU PORUKU...',
        submitRequest: 'Pošalji Zahtjev',
      },
      shipping: {
        classification: 'Protokol Distribucije',
        processingTime: 'Vrijeme Obrade',
        processingText:
          'Sve narudžbe se obrađuju u roku od 3-5 radnih dana. Narudžbe napravljene vikendom ili praznicima će biti obrađene sljedećeg radnog dana.',
        shippingMethods: 'Metode Dostave',
        standardShipping: 'Standardna Dostava: 7-14 radnih dana',
        expressShipping: 'Ekspresna Dostava: 3-5 radnih dana',
        internationalShipping: 'Međunarodna Dostava: 14-21 radni dan',
        shippingCosts: 'Troškovi Dostave',
        shippingCostsText:
          'Troškovi dostave se izračunavaju pri plaćanju na osnovu odredišta i odabrane metode dostave.',
        trackingInfo: 'Informacije o Praćenju',
        trackingInfoText:
          'Kada vaša narudžba bude poslana, primit ćete email za potvrdu s informacijama o praćenju. Molimo dopustite 24 sata za aktivaciju praćenja.',
        internationalCustoms: 'Međunarodna Carina',
        internationalCustomsText:
          'Međunarodne narudžbe mogu biti podložne uvoznim carinama i porezima. Primalac je odgovoran za sve carinske troškove ili uvozne poreze nametnute od strane njihove države.',
      },
      returns: {
        classification: 'Dokumentacija Povrata',
        returnWindow: 'Period za Povrat',
        returnWindowText:
          'Artikli se mogu vratiti u roku od 30 dana od dostave za puni povrat novca. Artikli moraju biti nenošeni, neопрани i u izvornom stanju sa svim etiketama.',
        returnProcess: 'Proces Povrata',
        returnStep1: 'Kontaktirajte naš tim za podršku na returns@borninyu.com',
        returnStep2: 'Primite ovlaštenje za povrat i naljepnicu za slanje',
        returnStep3: 'Sigurno upakujte artikal s izvornim pakovanjem',
        returnStep4: 'Pošaljite u roku od 7 dana od prijema ovlaštenja',
        returnStep5: 'Povrat novca obrađen u roku od 5-7 radnih dana od prijema',
        nonReturnable: 'Artikli Koji se Ne Mogu Vratiti',
        nonReturnableItem1: 'Prilagođeni ili personalizirani artikli',
        nonReturnableItem2: 'Artikli na rasprodaji ili sniženju',
        nonReturnableItem3: 'Artikli oštećeni od strane kupca',
        exchanges: 'Zamjene',
        exchangesText:
          'Trenutno ne nudimo izravne zamjene. Molimo vratite artikal za povrat novca i napravite novu narudžbu za željeni artikal.',
        damagedItems: 'Oštećeni ili Neispravni Artikli',
        damagedItemsText:
          'Ako dobijete oštećen ili neispravan artikal, molimo kontaktirajte nas odmah na support@borninyu.com s fotografijama artikla i pakiranja. Organizirat ćemo zamjenu ili puni povrat novca bez troškova za vas.',
      },
      privacy: {
        classification: 'Povjerljiv Dokument',
        lastUpdated: 'Zadnje Ažurirano: Novembar 2024',
        infoWeCollect: 'Informacije Koje Prikupljamo',
        infoWeCollectText:
          'Prikupljamo informacije koje izravno pružate, kao što su ime, email, adresa za dostavu i informacije o plaćanju kada obavljate kupnju ili kreirate račun.',
        howWeUse: 'Kako Koristimo Vaše Informacije',
        useReason1: 'Obrađujemo i ispunjavamo vaše narudžbe',
        useReason2: 'Šaljemo potvrde narudžbi i ažuriranja o dostavi',
        useReason3: 'Odgovaramo na vaše upite i zahtjeve za podršku',
        useReason4: 'Šaljemo promotivne emailove (uz vašu suglasnost)',
        useReason5: 'Poboljšavamo našu web stranicu i korisničko iskustvo',
        infoSharing: 'Dijeljenje Informacija',
        infoSharingText:
          'Ne prodajemo niti iznajmljujemo vaše osobne informacije trećim stranama. Možemo dijeliti vaše informacije s pružateljima usluga koji pomažu u radu naše web stranice, obradi plaćanja i ispunjenju narudžbi.',
        dataSecurity: 'Sigurnost Podataka',
        dataSecurityText:
          'Primjenjujemo odgovarajuće sigurnosne mjere da zaštitimo vaše osobne informacije od neovlaštenog pristupa, izmjene ili otkrivanja.',
        yourRights: 'Vaša Prava',
        right1: 'Pristup osobnim podacima koje držimo o vama',
        right2: 'Zahtjev za ispravak netočnih podataka',
        right3: 'Zahtjev za brisanje vaših podataka',
        right4: 'Odjava s marketinških komunikacija',
        contactUs: 'Kontaktirajte Nas',
        contactUsText: 'Za upite vezane uz privatnost, kontaktirajte nas na privacy@borninyu.com',
      },
      terms: {
        classification: 'Pravna Dokumentacija',
        lastUpdated: 'Zadnje Ažurirano: Novembar 2024',
        acceptance: 'Prihvaćanje Uvjeta',
        acceptanceText:
          'Pristupanjem i korištenjem ove web stranice, prihvaćate i slažete se da budete vezani uvjetima i odredbama ovog sporazuma.',
        useLicense: 'Licenca za Upotrebu',
        useLicenseText:
          'Dozvola je dana za privremeno preuzimanje jedne kopije materijala s web stranice BORN IN YU samo za osobni, nekomercijalni privremeni pregled.',
        prohibited: 'Zabranjena Upotreba',
        prohibitedItem1: 'Kršenje bilo kojih važećih zakona ili propisa',
        prohibitedItem2: 'Lažno predstavljanje kao druga osoba ili entitet',
        prohibitedItem3: 'Ometanje sigurnosnih funkcija web stranice',
        prohibitedItem4: 'Korištenje automatiziranih sistema za pristup sajtu',
        productInfo: 'Informacije o Proizvodu',
        productInfoText:
          'Trudimo se da prikažemo boje i slike proizvoda što je točnije moguće. Međutim, ne možemo garantirati da će zaslon vašeg uređaja točno odražavati stvarnu boju proizvoda.',
        pricing: 'Cijene',
        pricingText:
          'Sve cijene podliježu promjenama bez najave. Zadržavamo pravo da modificiramo ili ukinemo proizvode bez najave.',
        limitation: 'Ograničenje Odgovornosti',
        limitationText:
          'BORN IN YU neće biti odgovoran za bilo kakvu neizravnu, slučajnu, posebnu ili posljedičnu štetu koja proizlazi iz vaše upotrebe web stranice ili proizvoda.',
        governingLaw: 'Mjerodavno Pravo',
        governingLawText:
          'Ovi uvjeti će biti regulirani i tumačeni u skladu s primjenjivim zakonima međunarodne trgovine.',
      },
      footer: {
        tagline: 'Službena Državna Arhiva Robe i Odjeće',
        quickLinks: 'Brze Veze',
        legal: 'Pravno',
        connect: 'Povežite Se',
        newsletter: 'Pretplata na Bilten',
        newsletterText: 'Pretplatite se da primite službene dokumente i ažuriranja arhive.',
        emailPlaceholder: 'Unesite službenu email adresu',
        subscribe: 'Pošalji Prijavu',
        links: {
          about: 'O Arhivi',
          products: 'Katalog Proizvoda',
          contact: 'Kontakt Biro',
          archive: 'Arhiva',
          manifesto: 'Manifest 1943',
          shipping: 'Protokol Dostave',
          returns: 'Politika Povrata',
          privacy: 'Povelja o Privatnosti',
          terms: 'Uslovi Korištenja',
          dossier: 'Modernistički Dosje',
          spomenikMaps: 'Mapa Spomenika',
        },
        copyright: '© 1943-2024 BORN IN YU. Vječna Prava Arhive.',
      },
      backToTop: 'Nazad na Vrh',
      dossier: {
        classification: 'ARHIVA DIZAJNA - KLASIFICIRANO',
        totalEntries: 'UKUPNO UNOSA',
        title: 'MODERNISTIČKI DOSJE',
        subtitle: 'Kolekcija jugoslovenskog modernističkog dizajna. Dizajn proizvoda, grafički dizajn, industrijska dostignuća i namještaj koji su definisali jednu eru.',
        fileNo: 'BROJ DOSIJEA',
        designer: 'DIZAJNER',
        year: 'GODINA',
        location: 'LOKACIJA',
        confidential: 'POVJERLJIVO',
        noResults: 'NEMA UNOSA',
        historicalDocuments: 'HISTORIJSKI DOKUMENTI',
        articlesLabel: 'ČLANCI',
        noResultsDescription: 'Nema dizajnerskih unosa za odabranu kategoriju.',
        categories: {
          all: 'SVI DIZAJNI',
          product: 'PROIZVOD',
          graphic: 'GRAFIKA',
          industrial: 'INDUSTRIJA',
          furniture: 'NAMJEŠTAJ',
        },
        status: { preserved: 'OČUVANO', deteriorating: 'PROPADA', destroyed: 'UNIŠTENO' },
        entries: {
          '1': {
            title: 'Rade Končar Električna Lokomotiva',
            description:
              'Serija 441/461 električna lokomotiva dizajnirana za Jugoslavenske Željeznice.',
            location: 'Zagreb, Hrvatska',
          },
          '2': {
            title: 'Yugo-nostalgija Poster Serija',
            description: 'Ikonična grafička kampanja slavlja jugoslavenskog identiteta.',
            location: 'Beograd, Srbija',
          },
          '3': {
            title: 'Zastava 750 "Fića" Dizajn',
            description: 'Narodni auto Jugoslavije.',
            location: 'Kragujevac, Srbija',
          },
          '4': {
            title: 'Rex Stolica Niko Kralj',
            description: 'Slagajuća šper-ploča stolica dizajnirana 1956.',
            location: 'Ljubljana, Slovenija',
          },
          '5': {
            title: "Sarajevo '84 Olimpijski Identitet",
            description: 'Kompletan vizualni identitet za ZOI 1984.',
            location: 'Sarajevo, Bosna',
          },
          '6': {
            title: 'Gorenje Retro Hladnjak',
            description: 'Kućanski aparat kombinujući zakrivljene forme.',
            location: 'Velenje, Slovenija',
          },
        },
        footer: { note: 'Svi dizajni autentificirani kroz Savezni Registar Dizajna' },
        modal: {
          confidential: 'POVJERLJIVO',
          fileNumber: 'BROJ DOSIJEA',
          designer: 'DIZAJNER',
          year: 'GODINA',
          location: 'LOKACIJA',
          description: 'OPIS',
          technicalSpecs: 'TEHNIČKE SPECIFIKACIJE',
          historicalContext: 'HISTORIJSKI KONTEKST',
          historicalContextDescription:
            'Ovaj dizajn predstavlja jugoslavenski samoupravni socijalizam s naglaskom na industrijsku modernizaciju i kulturni identitet.',
        },
      },
      blog: {
        backToDossier: 'Nazad na Dosje',
        relatedProducts: 'Pregledaj Povezane Proizvode',
        readTime: 'min čitanja',
        publishedOn: 'Objavljeno',
        author: 'Autor',
        category: 'Kategorija',
        tags: 'Oznake',
        imagePlaceholder: 'Slika Uskoro',
        navigation: {
          home: 'Početna',
          dossier: 'Dosje',
          article: 'Članak',
        },
      },
      spomenik: {
        totalMonuments: 'UKUPNO SPOMENIKA',
        title: 'MAPA SPOMENIKA',
        subtitle: 'Interaktivna dokumentacija jugoslavenskih partizanskih spomen-obilježja.',
        infoBox:
          'Spomenici su građeni širom Jugoslavije da obilježe mjesta bitaka iz Drugog svjetskog rata.',
        architect: 'ARHITEKTA',
        year: 'IZGRAĐENO',
        city: 'GRAD',
        coordinates: 'GPS',
        noResults: 'NEMA SPOMENIKA',
        noResultsDescription: 'Nema spomenika za odabranu državu.',
        countries: {
          all: 'SVE DRŽAVE',
          serbia: 'SRBIJA',
          croatia: 'HRVATSKA',
          bosnia: 'BOSNA I HERCEGOVINA',
          slovenia: 'SLOVENIJA',
          macedonia: 'SJEVERNA MAKEDONIJA',
          montenegro: 'CRNA GORA',
        },
        condition: {
          excellent: 'ODLIČNO',
          good: 'DOBRO',
          deteriorating: 'PROPADA',
          destroyed: 'UNIŠTENO',
        },
        monuments: {
          '1': {
            name: 'Jasenovac Kameni Cvijet',
            description: 'Gigantski betonski cvijet u sjećanje na žrtve logora Jasenovac.',
          },
          '2': {
            name: 'Podgarić Spomenik',
            description: 'Apstraktna krila koja se uzdižu iz zemlje.',
          },
          '3': {
            name: 'Sutjeska Spomenik Bitke',
            description: 'Dva masivna betonska krila predstavljaju Bitku na Sutjesci.',
          },
          '4': {
            name: 'Makedonium - Ilinden Memorijal',
            description: 'Jedinstvena kružna struktura s vitražima.',
          },
          '5': {
            name: 'Kameni Cvijet Kamenska',
            description: 'Memorijal u obliku lotosa dizajnirao Vojin Bakić.',
          },
          '6': {
            name: 'Partizansko Spomen Groblje Kolašin',
            description: 'Modernistički kompleks groblja.',
          },
        },
        legend: { title: 'LEGENDA STANJA' },
        modal: {
          viewOnMaps: 'PRIKAŽI NA GOOGLE KARTAMA',
          description: 'OPIS',
          visitingInfo: 'INFORMACIJE ZA POSJET',
          visitingDescription:
            'Većina spomenika se nalazi u javnim parkovima ili prirodnim okruženjima i dostupni su tijekom cijele godine. Molimo tretirajte ove spomenike s poštovanjem.',
        },
      },
      // Manifesto Page
      manifesto: {
        docId: 'ID DOKUMENTA',
        classified: 'DEKLASIFICIRANO',
        title: 'MANIFEST 1943',
        subtitle: 'Deklaracija Vječne Memorije',
        issued: 'IZDANO',
        approved: 'ODOBRENO',
        verified: 'VERIFICIRANO',
        article1: {
          title: 'Naša Misija',
          content:
            'Čuvamo sjećanje na naciju koja je prevazišla granice i vrijeme. Kroz autentičnu robu i arhivsku dokumentaciju, održavamo živim duh jugoslavenskog jedinstva, modernizma i kulturnih dostignuća. Ovo nije nostalgija—ovo je dokumentacija.',
        },
        article2: {
          title: 'Što Čuvamo',
          content:
            'Brutalističke spomenike koji diraju nebo. Socijalistički modernistički dizajn koji je definirao eru. Industrijska dostignuća koja su izgradila naciju. Kulturne artefakte koji su ujedinili šest republika. Betonske snove koji su oblikovali generacije. Sve autentificirano, sve dokumentirano, sve vječno.',
        },
        article3: {
          title: 'Zašto Se Sjećamo',
          content:
            'Država može nestati, ali njen duh ne može. Jugoslavensko iskustvo bilo je jedinstveno—federacija izgrađena na bratstvu i jedinstvu, izražena kroz radikalnu arhitekturu i progresivni dizajn. Dokumentiramo ovo naslijeđe prije nego što nestane iz sjećanja, osiguravajući da buduće generacije razumiju što je nekada bilo.',
        },
        article4: {
          title: 'Betonski Snovi',
          content:
            'Od Genex kule do Memorijala Sutjeska, od Pionir dvorane do Avalskog tornja—ove strukture bile su više od zgrada. Bile su izjave ambicije, jedinstva i modernističke vizije. Svaki spomenik priča priču o naciji koja se usudila sanjati u betonu i čeliku.',
        },
        article5: {
          title: 'Za Buduće Generacije',
          content:
            'Mi nismo povjesničari. Mi smo arhivisti sjećanja. Svaki proizvod koji kreiramo nosi težinu dokumentacije. Svaki dizajn referencira autentične izvore. Osiguravamo da oni rođeni nakon 1991. mogu dodirnuti, nositi i iskusiti materijalnu kulturu raspuštene nacije. Ova arhiva je vječna.',
        },
        footer: {
          authority: 'SAVEZNI ORGAN ZA OČUVANJE NASLIJEĐA',
          official: 'SLUŽBENO',
          declassified: 'DEKLASIFICIRANO',
          forPosterity: 'ZA POTOMSTVO',
        },
      },
      // Archive Page
      archive: {
        securityClearance: 'SIGURNOSNA DOZVOLA',
        totalArtifacts: 'UKUPNO ARTEFAKATA',
        title: 'ARHIVA',
        subtitle:
          'Kronološki repozitorij jugoslavenskih kulturnih artefakata, spomenika i dizajnerskog naslijeđa. Svi predmeti autentificirani i dokumentirani za očuvanje.',
        docId: 'ID DOK',
        year: 'GODINA',
        noResults: 'NEMA ARTEFAKATA',
        noResultsDescription:
          'Nema artefakata koji odgovaraju odabranoj kategoriji. Molimo pokušajte drugi filter.',
        categories: {
          all: 'SVE ARHIVE',
          architecture: 'ARHITEKTURA',
          design: 'DIZAJN',
          industry: 'INDUSTRIJA',
          culture: 'KULTURA',
          sports: 'SPORT',
        },
        status: {
          declassified: 'DEKLASIFICIRANO',
          classified: 'KLASIFICIRANO',
          restricted: 'OGRANIČENO',
        },
        artifacts: {
          '1': {
            title: 'Avala Toranj Komunikacijski Spomenik',
            description:
              'Kultni telekomunikacijski toranj izgrađen 1965, uništen 1999, rekonstruiran 2010. Simbol jugoslavenskog tehnološkog dostignuća i modernističkog dizajna.',
          },
          '2': {
            title: 'Yugo 45 Automobilski Plan',
            description:
              'Tehnička dokumentacija za kultni Yugo 45, proizveden od 1980-2008. Predstavlja jugoslavensku automobilsku industriju i masovnu proizvodnju.',
          },
          '3': {
            title: 'Genex Kula Arhitektonski Planovi',
            description:
              'Originalni planovi za Zapadna Vrata Beograda (Genex Kula), dizajnirao Mihajlo Mitrović. Završena 1980, stoji kao brutalistički orijentir Beograda.',
          },
          '4': {
            title: 'Sarajevo Zimske Olimpijade Amblem',
            description:
              'Službeni amblem i dizajnerski materijali sa Zimskih Olimpijskih igara 1984 održanih u Sarajevu. Slavi jugoslavensku organizacijsku izvrsnost i naslijeđe zimskih sportova.',
          },
          '5': {
            title: 'Sutjeska Memorijalni Kompleks',
            description:
              'Fotografska dokumentacija memorijalnog kompleksa Bitke na Sutjesci kipara Miodraga Živkovića. Završen 1971, obilježava partizansku pobjedu.',
          },
          '6': {
            title: 'Crvena Zvezda 1991 Europski Kup',
            description:
              'Arhivski materijali koji dokumentiraju pobjedu Crvene zvezde na Europskom kupu 1991, posljednje veliko jugoslavensko sportsko dostignuće prije raspada.',
          },
        },
      },
    },
  },
};

// Get saved language from localStorage or default to 'en'
const savedLanguage =
  typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') || 'en' : 'en';

i18n.use(initReactI18next).init({
  resources,
  lng: savedLanguage, // use saved language or default
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes values
  },
});

// Save language to localStorage whenever it changes
i18n.on('languageChanged', lng => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('i18nextLng', lng);
  }
});

export default i18n;
